import { useCallback, useEffect, useState } from 'react';
import { useUser } from '../providers/UserProvider';
import { listRecentSearches, type RecentSearch } from '../repositories/savedSearchRepository';

const NONE: RecentSearch[] = [];

/**
 * The signed-in account's recent searches (`/api/searches/recent`), newest
 * first; none when signed out. History belongs to an account: a list read for
 * another account, even one still in flight, is never shown. `refresh` reads
 * it again, e.g. after a search starts.
 */
export function useRecentSearches(): { items: RecentSearch[]; refresh: () => void } {
  const { isLoggedIn, user } = useUser();
  const owner = isLoggedIn ? user?.username ?? null : null;
  const [history, setHistory] = useState<{ owner: string; items: RecentSearch[] } | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!owner) return undefined;
    let disposed = false;
    listRecentSearches().then(items => { if (!disposed) setHistory({ owner, items }); }).catch(() => {});
    return () => { disposed = true; };
  }, [owner, revision]);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  return { items: history?.owner === owner && owner ? history.items : NONE, refresh };
}
